import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Users, 
  Search,
  Eye,
  MoreHorizontal,
  MessageSquare,
  Loader2,
  RefreshCw,
  Plane,
  CheckCircle,
  XCircle,
  HelpCircle,
  Clock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useMyPartnerProfile, usePartnerCandidates, useUpdateCandidateStatus, useUpdateIntegration } from '../lib/hooks';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../lib/translations';

const MyCandidatesPage = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('all');
  const [statusDialog, setStatusDialog] = useState({ open: false, candidate: null });
  const [integrationDialog, setIntegrationDialog] = useState({ open: false, candidate: null });
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [integrationStage, setIntegrationStage] = useState('');
  const [arrivalConfirmed, setArrivalConfirmed] = useState(false);
  const [arrivalDate, setArrivalDate] = useState('');
  const [integrationNotes, setIntegrationNotes] = useState('');

  const { data: profile, isLoading: profileLoading } = useMyPartnerProfile();
  const { data: candidates = [], isLoading: candidatesLoading } = usePartnerCandidates(profile?.id);
  const updateStatus = useUpdateCandidateStatus(profile?.id);
  const updateIntegration = useUpdateIntegration(profile?.id);

  const loading = profileLoading || candidatesLoading;

  const filteredCandidates = candidates.filter(c => {
    const matchesSearch = `${c.first_name} ${c.last_name} ${c.email || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.placement_status === statusFilter;
    const matchesStage = stageFilter === 'all' || c.integration_stage === stageFilter;
    return matchesSearch && matchesStatus && matchesStage;
  });

  const placementStatusLabels = {
    pending: 'En attente',
    accepted: 'Accepté',
    needs_info: 'Info requise',
    rejected: 'Refusé'
  };

  const placementStatusColors = {
    pending: 'bg-warning/10 text-warning',
    accepted: 'bg-success/10 text-success',
    needs_info: 'bg-primary/10 text-primary',
    rejected: 'bg-destructive/10 text-destructive'
  };

  const integrationStageLabels = {
    pre_departure: 'Pré-départ',
    arrived: 'Arrivé',
    settled: 'Installé',
    completed: 'Terminée'
  };

  const integrationStageColors = {
    pre_departure: 'bg-secondary/10 text-secondary',
    arrived: 'bg-primary/10 text-primary',
    settled: 'bg-accent/10 text-accent',
    completed: 'bg-success/10 text-success'
  };

  const handleOpenStatusDialog = (candidate) => {
    setStatusDialog({ open: true, candidate });
    setNewStatus(candidate.placement_status || 'pending');
    setStatusNotes(candidate.notes || '');
  };

  const handleUpdateStatus = async () => {
    if (!statusDialog.candidate) return;
    try {
      await updateStatus.mutateAsync({
        studentId: statusDialog.candidate.id,
        data: { status: newStatus, notes: statusNotes }
      });
      toast.success('Statut mis à jour');
      setStatusDialog({ open: false, candidate: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  const handleOpenIntegrationDialog = (candidate) => {
    setIntegrationDialog({ open: true, candidate });
    setIntegrationStage(candidate.integration_stage || 'pre_departure');
    setArrivalConfirmed(false);
    setArrivalDate('');
    setIntegrationNotes('');
  };

  const handleUpdateIntegration = async () => {
    if (!integrationDialog.candidate) return;
    try {
      await updateIntegration.mutateAsync({
        studentId: integrationDialog.candidate.id,
        data: { 
          stage: integrationStage, 
          arrival_confirmed: arrivalConfirmed,
          arrival_date: arrivalDate || null,
          notes: integrationNotes || null
        }
      });
      toast.success('Intégration mise à jour');
      setIntegrationDialog({ open: false, candidate: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-candidates-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Mes candidats</h1>
        <p className="text-muted-foreground mt-1">Gérez et suivez vos candidats assignés</p>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t("students_search", lang)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="candidates-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('all_statuses', lang)}</SelectItem>
                <SelectItem value="pending">{t('status_pending', lang)}</SelectItem>
                <SelectItem value="accepted">Accepté</SelectItem>
                <SelectItem value="needs_info">Info requise</SelectItem>
                <SelectItem value="rejected">Refusé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="stage-filter">
                <SelectValue placeholder="Étape" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les étapes</SelectItem>
                <SelectItem value="pre_departure">Pré-départ</SelectItem>
                <SelectItem value="arrived">{t('arrivals_status_arrived', lang)}</SelectItem>
                <SelectItem value="settled">{t('arrivals_status_settled', lang)}</SelectItem>
                <SelectItem value="completed">Terminée</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {filteredCandidates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-semibold text-lg mb-1">Aucun candidat trouvé</h3>
              <p className="text-muted-foreground">
                {candidates.length === 0 
                  ? "Vous n'avez pas encore de candidats assignés"
                  : "Modifiez vos filtres pour voir plus de résultats"
                }
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidat</TableHead>
                  <TableHead className="hidden md:table-cell">{t('dossier_detail_visa', lang)}</TableHead>
                  <TableHead>{lang === 'de' ? 'Akte' : 'Dossier'}</TableHead>
                  <TableHead>{t('students_status', lang)}</TableHead>
                  <TableHead>Intégration</TableHead>
                  <TableHead className="hidden lg:table-cell">{t('students_level', lang)}</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCandidates.map((candidate) => (
                  <TableRow key={candidate.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-semibold">
                            {candidate.first_name[0]}{candidate.last_name[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium">{candidate.first_name} {candidate.last_name}</p>
                          <p className="text-sm text-muted-foreground">{candidate.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm">{candidate.visa_objective || 'Non spécifié'}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              (candidate.dossier_completion || 0) >= 100 ? 'bg-success' :
                              (candidate.dossier_completion || 0) >= 50 ? 'bg-primary' : 'bg-warning'
                            }`}
                            style={{ width: `${candidate.dossier_completion || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {candidate.dossier_completion || 0}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={placementStatusColors[candidate.placement_status]}>
                        {placementStatusLabels[candidate.placement_status] || 'En attente'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={integrationStageColors[candidate.integration_stage]}>
                        {integrationStageLabels[candidate.integration_stage] || 'Pré-départ'}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant="outline">{candidate.german_level || 'N/A'}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`candidate-actions-${candidate.id}`}>
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleOpenStatusDialog(candidate)}>
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Changer le statut
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleOpenIntegrationDialog(candidate)}>
                            <Plane className="w-4 h-4 mr-2" />
                            Suivi d'intégration
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate('/messages')}>
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Envoyer un message
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Summary Card */}
      <Card className="border-0 shadow-sm bg-secondary/5">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold font-heading">{candidates.length}</p>
              <p className="text-sm text-muted-foreground">Total candidats</p>
            </div>
            <div>
              <p className="text-2xl font-bold font-heading text-success">
                {candidates.filter(c => (c.dossier_completion || 0) >= 100).length}
              </p>
              <p className="text-sm text-muted-foreground">Dossiers complets</p>
            </div>
            <div>
              <p className="text-2xl font-bold font-heading text-warning">
                {candidates.filter(c => c.placement_status === 'pending').length}
              </p>
              <p className="text-sm text-muted-foreground">{t('status_pending', lang)}</p>
            </div>
            <div>
              <p className="text-2xl font-bold font-heading text-primary">
                {candidates.filter(c => c.placement_status === 'accepted').length}
              </p>
              <p className="text-sm text-muted-foreground">Acceptés</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog.open} onOpenChange={(open) => setStatusDialog({ open, candidate: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Mettre à jour le statut</DialogTitle>
            <DialogDescription>
              Candidat: {statusDialog.candidate?.first_name} {statusDialog.candidate?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statut de la candidature</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-warning" />
                      En attente
                    </div>
                  </SelectItem>
                  <SelectItem value="accepted">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-success" />
                      Accepté
                    </div>
                  </SelectItem>
                  <SelectItem value="needs_info">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-primary" />
                      Information requise
                    </div>
                  </SelectItem>
                  <SelectItem value="rejected">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-destructive" />
                      Refusé
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Ajouter des notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog({ open: false, candidate: null })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateStatus} disabled={updateStatus.isPending}>
              {updateStatus.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Integration Update Dialog */}
      <Dialog open={integrationDialog.open} onOpenChange={(open) => setIntegrationDialog({ open, candidate: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Suivi d'intégration</DialogTitle>
            <DialogDescription>
              Candidat: {integrationDialog.candidate?.first_name} {integrationDialog.candidate?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Étape d'intégration</Label>
              <Select value={integrationStage} onValueChange={setIntegrationStage}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_departure">Pré-départ</SelectItem>
                  <SelectItem value="arrived">Arrivé en Allemagne</SelectItem>
                  <SelectItem value="settled">{t('arrivals_status_settled', lang)}</SelectItem>
                  <SelectItem value="completed">Intégration terminée</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="arrival-confirmed"
                checked={arrivalConfirmed}
                onChange={(e) => setArrivalConfirmed(e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="arrival-confirmed">Confirmer l'arrivée</Label>
            </div>
            {arrivalConfirmed && (
              <div className="space-y-2">
                <Label>Date d'arrivée</Label>
                <Input
                  type="date"
                  value={arrivalDate}
                  onChange={(e) => setArrivalDate(e.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={integrationNotes}
                onChange={(e) => setIntegrationNotes(e.target.value)}
                placeholder="Notes sur l'intégration..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIntegrationDialog({ open: false, candidate: null })}>
              Annuler
            </Button>
            <Button onClick={handleUpdateIntegration} disabled={updateIntegration.isPending}>
              {updateIntegration.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyCandidatesPage;
